import { SyntheticEvent, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { RootState } from 'src/store/reducers';

import * as actions from '@redux-actions/';

import {
    TextField,
    InputLabel,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    FormHelperText,
    Radio,
    InputAdornment,
} from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';

import ArrowDown from '@images/svg-icons/arrowDown.svg';
import ErrorIcon from '@images/svg-icons/errorIcon.svg';
import SuccessIcon from '@images/svg-icons/successIcon.svg';
import CloseIcon from '@images/svg-icons/closeIcon.svg';

import { Controller, useFormContext } from 'react-hook-form';

import {
    GeographicInformationsPayload,
    PersonalDetailsFormStatus,
    PrimaryFormFields,
    SecondaryFormFields,
    ThirdFormFields,
} from '@interfaces/myProfileInterfaces';

import { getPersonalDetailsCityAndState } from '@services/myProfileServices';

import { capitalizeFirstLetter, getLabelFallback } from '@utils/utils';
import { replaceLabelPlaceholder } from '@utils/pages-utils/EyeExamsUtils';

export interface Props {
    defaultFormValues: PrimaryFormFields & SecondaryFormFields & ThirdFormFields;
}

const PersonalDetailsPrimaryForm = ({ defaultFormValues }: Props): JSX.Element => {
    const {
        register,
        watch,
        control,
        setValue,
        setError,
        formState: { errors, isSubmitting },
        clearErrors,
    } = useFormContext();

    const watchBillingFirstName = watch('billingFirstName');
    const watchBillingLastName = watch('billingLastName');
    const watchBillingAddressLine1 = watch('billingAddressLine1');
    const watchBillingAddressLine2 = watch('billingAddressLine2');
    const watchBillingZipCode = watch('billingZipcode');
    const watchBillingCity = watch('billingCity');
    const watchShippingFirstName = watch('shippingFirstName');
    const watchShippingLastName = watch('shippingLastName');
    const watchShippingAddressLine1 = watch('shippingAddressLine1');
    const watchShippingAddressLine2 = watch('shippingAddressLine2');
    const watchShippingZipCode = watch('shippingZipcode');
    const watchShippingCity = watch('shippingCity');
    const watchShipToBillingAddress = watch('shipToBillingAddress');

    const initialFormStatus: PersonalDetailsFormStatus<PrimaryFormFields> = {
        billingFirstName: {
            focus: false,
            success: watchBillingFirstName ? true : false,
        },
        billingLastName: {
            focus: false,
            success: watchBillingLastName ? true : false,
        },
        billingAddressLine1: {
            focus: false,
            success: watchBillingAddressLine1 ? true : false,
        },
        billingAddressLine2: {
            focus: false,
            success: watchBillingAddressLine2 ? true : false,
        },
        billingZipcode: {
            focus: false,
            success: watchBillingZipCode ? true : false,
        },
        billingCity: {
            focus: false,
            success: watchBillingCity ? true : false,
        },
        billingState: {
            focus: false,
            success: false,
        },
        shipToBillingAddress: {
            focus: false,
            success: false,
        },
        shippingFirstName: {
            focus: false,
            success: watchShippingFirstName ? true : false,
        },
        shippingLastName: {
            focus: false,
            success: watchShippingLastName ? true : false,
        },
        shippingAddressLine1: {
            focus: false,
            success: watchShippingAddressLine1 ? true : false,
        },
        shippingAddressLine2: {
            focus: false,
            success: watchShippingAddressLine2 ? true : false,
        },
        shippingZipcode: {
            focus: false,
            success: watchShippingZipCode ? true : false,
        },
        shippingCity: {
            focus: false,
            success: watchShippingCity ? true : false,
        },
        shippingState: {
            focus: false,
            success: false,
        },
    };

    const [showFormFields, setShowFormFields] = useState<{
        billingAddressLine: boolean;
        shippingAddressLine: boolean;
    }>({
        billingAddressLine: watchBillingAddressLine2 ? true : false,
        shippingAddressLine: watchShippingAddressLine2 ? true : false,
    });

    const [shipToBillingAddress, setShipToBillingAddress] = useState<boolean>(
        watchShipToBillingAddress ?? true
    );

    const [formFieldStatus, setFormFieldStatus] =
        useState<PersonalDetailsFormStatus<PrimaryFormFields>>(initialFormStatus);

    const dispatch = useDispatch();

    const config = useSelector((state: RootState) => state.common.config);
    const geographicInformations = useSelector(
        (state: RootState) => state.myProfile.geographicInformations
    );

    useEffect(() => {
        if (geographicInformations) {
            const { city, state, addressType } = geographicInformations;
            addressType === 'billingAddress'
                ? (setValue('billingState', state),
                  setValue('billingCity', city),
                  setFormFieldStatus({
                      ...formFieldStatus,
                      billingCity: { success: true, focus: false },
                  }),
                  clearErrors(['billingState', 'billingCity']))
                : (setValue('shippingState', state),
                  setValue('shippingCity', city),
                  setFormFieldStatus({
                      ...formFieldStatus,
                      shippingCity: { success: true, focus: false },
                  }),
                  clearErrors(['shippingState', 'shippingCity']));
        }
        return () => {
            dispatch(actions.getGeographicInformationsSuccess(null));
        };
    }, [geographicInformations]);

    useEffect(() => {
        Boolean(shipToBillingAddress) && resetShippingAddressInformations();
    }, [shipToBillingAddress]);

    /**The function below reset the shipping address informations */

    const resetShippingAddressInformations = () => {
        clearErrors([
            'shippingFirstName',
            'shippingLastName',
            'shippingAddressLine1',
            'shippingZipcode',
            'shippingCity',
            'shippingState',
        ]),
            setValue('shippingFirstName', defaultFormValues.shippingFirstName),
            setValue('shippingLastName', defaultFormValues.shippingLastName),
            setValue('shippingAddressLine1', defaultFormValues.shippingAddressLine1),
            setValue('shippingAddressLine2', defaultFormValues.shippingAddressLine2),
            setValue('shippingZipcode', defaultFormValues.shippingZipcode),
            setValue('shippingCity', defaultFormValues.shippingCity),
            setValue('shippingState', defaultFormValues.shippingState),
            setFormFieldStatus({
                ...formFieldStatus,
                ...(defaultFormValues.shippingFirstName && {
                    shippingFirstName: { focus: false, success: true },
                }),
                ...(defaultFormValues.shippingLastName && {
                    shippingLastName: { focus: false, success: true },
                }),
                ...(defaultFormValues.shippingAddressLine1 && {
                    shippingAddressLine1: { focus: false, success: true },
                }),
                ...(defaultFormValues.shippingAddressLine2 && {
                    shippingAddressLine2: { focus: false, success: true },
                }),
                ...(defaultFormValues.shippingZipcode && {
                    shippingZipcode: { focus: false, success: true },
                }),
                ...(defaultFormValues.shippingCity && {
                    shippingCity: { focus: false, success: true },
                }),
                ...(defaultFormValues.shippingState && {
                    shippingState: { focus: false, success: true },
                }),
            });
    };

    /** The function below handles the onBlur, onChange e onFocus events for the form validation */

    const handleInputEvent = (e: SyntheticEvent) => {
        e.persist();
        const event = e.target as HTMLInputElement;
        !isSubmitting && clearErrors(event.name);

        if (['billingZipcode', 'shippingZipcode'].includes(event.name)) {
            if (e.type === 'blur') {
                const geographicInfoPayload: GeographicInformationsPayload = {
                    isMockRequest: config.mockRequest,
                    zipCode: event.value,
                    addressType:
                        event.name === 'billingZipcode' ? 'billingAddress' : 'shippingAddress',
                };
                event.value.length < event.minLength ||
                (config.countryName === 'CA' &&
                    !/^[A-Za-z]\d[A-Za-z][ ]\d[A-Za-z]\d$/.test(event.value))
                    ? (setFormFieldStatus({
                          ...formFieldStatus,
                          [event.name]: { success: false, focus: false },
                      }),
                      setValue(event.name, event.value),
                      setError(event.name, {
                          type: 'minLength',
                          message: getLabelFallback(
                              event.name === 'billingZipcode'
                                  ? config.countryName === 'CA'
                                      ? 'MYPROFILE_PERSONAL_DETAILS_INVALID_BILLING_POSTAL_CODE_MESSAGE'
                                      : 'MYPROFILE_PERSONAL_DETAILS_INVALID_BILLING_ZIP_CODE_MESSAGE'
                                  : config.countryName === 'CA'
                                  ? 'MYPROFILE_PERSONAL_DETAILS_INVALID_SHIPPING_POSTAL_CODE_MESSAGE'
                                  : 'MYPROFILE_PERSONAL_DETAILS_INVALID_SHIPPING_ZIP_CODE_MESSAGE'
                          ),
                      }))
                    : dispatch(getPersonalDetailsCityAndState(geographicInfoPayload));
            }
            if (e.type === 'change') {
                setValue(event.name, event.value ? event.value.slice(0, event.maxLength) : null);
            }
        }

        if (
            [
                'billingFirstName',
                'billingLastName',
                'shippingFirstName',
                'shippingLastName',
            ].includes(event.name) &&
            e.type == 'blur' &&
            event.value.length &&
            /\d/.test(event.value)
        ) {
            return (
                setFormFieldStatus({
                    ...formFieldStatus,
                    [event.name]: {
                        success: false,
                        focus: false,
                    },
                }),
                setError(event.name, {
                    type: 'pattern',
                    message: getLabelFallback(
                        `MYPROFILE_PERSONAL_DETAILS_FORM_FIELD_INVALID_${event.name.toUpperCase()}`
                    ),
                })
            );
        }
        event.value
            ? (setFormFieldStatus({
                  ...formFieldStatus,
                  [event.name]: {
                      success: e.type === 'blur' ? true : e.type === 'change' ? false : false,
                      focus:
                          e.type === 'blur'
                              ? false
                              : e.type === 'change'
                              ? true
                              : e.type === 'focus' && !errors[event.name]
                              ? true
                              : false,
                  },
              }),
              setValue(event.name, event.value))
            : !['billingAddressLine2', 'shippingAddressLine2'].includes(event.name)
            ? (setFormFieldStatus({
                  ...formFieldStatus,
                  [event.name]: { success: false, focus: false },
              }),
              setValue(event.name, ''),
              e.type === 'blur' &&
                  setError(event.name, {
                      type: 'required',
                      message: replaceLabelPlaceholder(
                          'MYPROFILE_PERSONAL_DETAILS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                          '{FORM_FIELD}',
                          getLabelFallback(
                              getErrorMessage(event.name as keyof PrimaryFormFields)
                          )?.toLowerCase()
                      ),
                  }))
            : setValue(event.name, '');
    };

    /** The function below returns the form field error message */

    const getErrorMessage = (field: keyof PrimaryFormFields): string => {
        switch (field.toUpperCase()) {
            case 'BILLINGZIPCODE':
                return config.countryName === 'CA'
                    ? 'MYPROFILE_PERSONAL_DETAILS_FORM_BILLINGZIPCODE_CA_LABEL'
                    : 'MYPROFILE_PERSONAL_DETAILS_FORM_BILLINGZIPCODE_OTHER_COUNTRIES_LABEL';
            case 'SHIPPINGZIPCODE':
                return config.countryName === 'CA'
                    ? 'MYPROFILE_PERSONAL_DETAILS_FORM_SHIPPINGPOSTALCODE_CA_LABEL'
                    : 'MYPROFILE_PERSONAL_DETAILS_FORM_SHIPPINGZIPCODE_OTHER_COUNTRIES_LABEL';
            default:
                return `MYPROFILE_PERSONAL_DETAILS_FORM_${field.toUpperCase()}_LABEL`;
        }
    };

    return (
        <div className="primary-form">
            <div className="fullname">
                <span className="secondary-message large bold align-left">
                    {getLabelFallback('MYPROFILE_PERSONAL_DETAILS_PRIMARY_FORM_FULL_NAME')}
                </span>
                <div className="form-group form-group_firstname">
                    <TextField
                        {...register('billingFirstName')}
                        className={`${
                            errors?.billingFirstName?.message
                                ? 'form-field-error'
                                : formFieldStatus.billingFirstName.success
                                ? 'form-field-success'
                                : ''
                        }`}
                        onChange={handleInputEvent}
                        onFocus={handleInputEvent}
                        onBlur={handleInputEvent}
                        name="billingFirstName"
                        variant="outlined"
                        label={capitalizeFirstLetter(
                            getLabelFallback(
                                'MYPROFILE_PERSONAL_DETAILS_FORM_BILLINGFIRSTNAME_LABEL'
                            )
                        )}
                        helperText={errors?.billingFirstName?.message}
                        InputProps={{
                            endAdornment: formFieldStatus.billingFirstName.focus ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <CloseIcon
                                        className="cursor-pointer"
                                        onMouseDown={() => setValue('billingFirstName', '')}
                                    />
                                </InputAdornment>
                            ) : errors?.billingFirstName?.message ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <ErrorIcon />
                                </InputAdornment>
                            ) : formFieldStatus.billingFirstName.success ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <SuccessIcon />
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                    <TextField
                        {...register('billingLastName')}
                        className={`${
                            errors?.billingLastName?.message
                                ? 'form-field-error'
                                : formFieldStatus.billingLastName.success
                                ? 'form-field-success'
                                : ''
                        }`}
                        onChange={handleInputEvent}
                        onFocus={handleInputEvent}
                        onBlur={handleInputEvent}
                        name="billingLastName"
                        variant="outlined"
                        label={capitalizeFirstLetter(
                            getLabelFallback(
                                'MYPROFILE_PERSONAL_DETAILS_FORM_BILLINGLASTNAME_LABEL'
                            )
                        )}
                        helperText={errors?.billingLastName?.message}
                        InputProps={{
                            endAdornment: formFieldStatus.billingLastName.focus ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <CloseIcon
                                        className="cursor-pointer"
                                        onMouseDown={() => setValue('billingLastName', '')}
                                    />
                                </InputAdornment>
                            ) : errors?.billingLastName?.message ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <ErrorIcon />
                                </InputAdornment>
                            ) : formFieldStatus.billingLastName.success ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <SuccessIcon />
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                </div>
            </div>
            <div className="billing-address">
                <span className="secondary-message large bold align-left">
                    {getLabelFallback('MYPROFILE_PERSONAL_DETAILS_PRIMARY_FORM_BILLING_ADDRESS')}
                </span>
                <div className="form-group form-group_billing-address">
                    <TextField
                        {...register('billingAddressLine1')}
                        className={`address-line_1 ${
                            errors?.billingAddressLine1?.message
                                ? 'form-field-error'
                                : formFieldStatus.billingAddressLine1.success
                                ? 'form-field-success'
                                : ''
                        }`}
                        onChange={handleInputEvent}
                        onFocus={handleInputEvent}
                        onBlur={handleInputEvent}
                        name="billingAddressLine1"
                        variant="outlined"
                        label={capitalizeFirstLetter(
                            replaceLabelPlaceholder(
                                'MYPROFILE_PERSONAL_DETAILS_PRIMARY_FORM_BILLING_ADDRESS_LINE',
                                '{LINE_NUMBER}',
                                '1'
                            )
                        )}
                        helperText={errors?.billingAddressLine1?.message}
                        InputProps={{
                            endAdornment: formFieldStatus.billingAddressLine1.focus ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <CloseIcon
                                        className="cursor-pointer"
                                        onMouseDown={() => setValue('billingAddressLine1', '')}
                                    />
                                </InputAdornment>
                            ) : errors?.billingAddressLine1?.message ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <ErrorIcon />
                                </InputAdornment>
                            ) : formFieldStatus.billingAddressLine1.success ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <SuccessIcon />
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                    {!showFormFields.billingAddressLine && (
                        <a
                            className="mb-32"
                            onClick={() =>
                                setShowFormFields({
                                    ...showFormFields,
                                    billingAddressLine: true,
                                })
                            }
                        >
                            {getLabelFallback(
                                'MYPROFILE_PERSONAL_DETAILS_PRIMARY_FORM_BILLING_ADD_SECOND_ADDRESS_CTA'
                            )}
                        </a>
                    )}
                    {showFormFields.billingAddressLine && (
                        <TextField
                            {...register('billingAddressLine2')}
                            className={`address-line_2 ${
                                formFieldStatus.billingAddressLine2.success
                                    ? 'form-field-success'
                                    : ''
                            }`}
                            onChange={handleInputEvent}
                            onFocus={handleInputEvent}
                            onBlur={handleInputEvent}
                            name="billingAddressLine2"
                            variant="outlined"
                            label={capitalizeFirstLetter(
                                replaceLabelPlaceholder(
                                    'MYPROFILE_PERSONAL_DETAILS_PRIMARY_FORM_BILLING_ADDRESS_LINE',
                                    '{LINE_NUMBER}',
                                    '2'
                                )
                            )}
                            value={watchBillingAddressLine2}
                            InputProps={{
                                endAdornment:
                                    formFieldStatus.billingAddressLine2.focus &&
                                    watchBillingAddressLine2 ? (
                                        <InputAdornment position="end" sx={{ width: '25px' }}>
                                            <CloseIcon
                                                className="cursor-pointer"
                                                onMouseDown={() =>
                                                    setValue('billingAddressLine2', '')
                                                }
                                            />
                                        </InputAdornment>
                                    ) : formFieldStatus.billingAddressLine2.success ? (
                                        <InputAdornment position="end" sx={{ width: '25px' }}>
                                            <SuccessIcon />
                                        </InputAdornment>
                                    ) : null,
                            }}
                        />
                    )}
                    <TextField
                        {...register('billingZipcode')}
                        className={`mb-40 small-field ${
                            errors?.billingZipcode?.message
                                ? 'form-field-error'
                                : formFieldStatus.billingZipcode.success
                                ? 'form-field-success'
                                : ''
                        }`}
                        onChange={handleInputEvent}
                        onFocus={handleInputEvent}
                        onBlur={handleInputEvent}
                        name="billingZipcode"
                        variant="outlined"
                        type={`${config.countryName.toUpperCase() === 'CA' ? 'text' : 'number'}`}
                        label={getLabelFallback(
                            config.countryName.toUpperCase() === 'CA'
                                ? 'MYPROFILE_PERSONAL_DETAILS_FORM_BILLINGZIPCODE_CA_LABEL'
                                : 'MYPROFILE_PERSONAL_DETAILS_FORM_BILLINGZIPCODE_OTHER_COUNTRIES_LABEL'
                        )}
                        helperText={errors?.billingZipcode?.message}
                        inputProps={{
                            minLength: `${config.countryName.toUpperCase() === 'CA' ? 7 : 5}`,
                            maxLength: `${config.countryName.toUpperCase() === 'CA' ? 7 : 5}`,
                        }}
                        InputProps={{
                            endAdornment: formFieldStatus.billingZipcode.focus ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <CloseIcon
                                        className="cursor-pointer"
                                        onMouseDown={() => setValue('billingZipcode', '')}
                                    />
                                </InputAdornment>
                            ) : errors?.billingZipcode?.message ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <ErrorIcon />
                                </InputAdornment>
                            ) : formFieldStatus.billingZipcode.success ? (
                                <InputAdornment position="end" sx={{ width: '25px' }}>
                                    <SuccessIcon />
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                    <div className="billing-address_city">
                        <TextField
                            {...register('billingCity')}
                            className={`${
                                errors?.billingCity?.message
                                    ? 'form-field-error'
                                    : formFieldStatus.billingCity.success
                                    ? 'form-field-success'
                                    : ''
                            }`}
                            value={watchBillingCity}
                            onChange={handleInputEvent}
                            onFocus={handleInputEvent}
                            onBlur={handleInputEvent}
                            name="billingCity"
                            variant="outlined"
                            label={capitalizeFirstLetter(
                                getLabelFallback(
                                    'MYPROFILE_PERSONAL_DETAILS_FORM_BILLINGCITY_LABEL'
                                )
                            )}
                            helperText={errors?.billingCity?.message}
                            InputProps={{
                                endAdornment: formFieldStatus.billingCity.focus ? (
                                    <InputAdornment position="end" sx={{ width: '25px' }}>
                                        <CloseIcon
                                            className="cursor-pointer"
                                            onMouseDown={() => setValue('billingCity', '')}
                                        />
                                    </InputAdornment>
                                ) : errors?.billingCity?.message ? (
                                    <InputAdornment position="end" sx={{ width: '25px' }}>
                                        <ErrorIcon />
                                    </InputAdornment>
                                ) : formFieldStatus.billingCity.success ? (
                                    <InputAdornment position="end" sx={{ width: '25px' }}>
                                        <SuccessIcon />
                                    </InputAdornment>
                                ) : null,
                            }}
                        />

                        <Controller
                            name="billingState"
                            control={control}
                            render={({ field: { value }, fieldState, field }) => (
                                <FormControl>
                                    <InputLabel id="billing-state-label">
                                        {capitalizeFirstLetter(
                                            getLabelFallback(
                                                config.countryName === 'CA'
                                                    ? 'MYPROFILE_PERSONAL_DETAILS_PRIMARY_FORM_BILLING_PROVINCES_LABEL'
                                                    : 'MYPROFILE_PERSONAL_DETAILS_PRIMARY_FORM_BILLING_STATE_LABEL'
                                            )
                                        )}
                                    </InputLabel>
                                    <Select
                                        className={`billing-state ${
                                            fieldState?.error?.message
                                                ? 'form-field-error'
                                                : value
                                                ? 'form-field-success'
                                                : ''
                                        }`}
                                        name="billingState"
                                        labelId="billing-state-label"
                                        variant="outlined"
                                        IconComponent={props => <ArrowDown {...props} />}
                                        label={capitalizeFirstLetter(
                                            getLabelFallback(
                                                config.countryName === 'CA'
                                                    ? 'MYPROFILE_PERSONAL_DETAILS_PRIMARY_FORM_BILLING_PROVINCES_LABEL'
                                                    : 'MYPROFILE_PERSONAL_DETAILS_PRIMARY_FORM_BILLING_STATE_LABEL'
                                            )
                                        )}
                                        endAdornment={
                                            fieldState?.error?.message ? (
                                                <InputAdornment
                                                    position="end"
                                                    sx={{
                                                        width: '25px',
                                                        position: 'absolute',
                                                        right: '45px',
                                                    }}
                                                >
                                                    <ErrorIcon />
                                                </InputAdornment>
                                            ) : value ? (
                                                <InputAdornment
                                                    position="end"
                                                    sx={{
                                                        width: '25px',
                                                        position: 'absolute',
                                                        right: '45px',
                                                    }}
                                                >
                                                    <SuccessIcon />
                                                </InputAdornment>
                                            ) : null
                                        }
                                        {...field}
                                    >
                                        {config.stateList.map(option => (
                                            <MenuItem
                                                key={option.code}
                                                value={option.code}
                                                disabled={option.disabled}
                                            >
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {errors?.billingState?.message && (
                                        <FormHelperText className="error-helper-text">
                                            {errors?.billingState?.message}
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            )}
                        />
                    </div>
                </div>
            </div>
            <div className="shipping-address">
                <FormControl>
                    <FormLabel
                        id="shipping-address-label"
                        className="secondary-message large bold align-left mb-16"
                    >
                        {getLabelFallback(
                            'MYPROFILE_PERSONAL_DETAILS_PRIMARY_FORM_SHIPPING_ADDRESS'
                        )}
                    </FormLabel>
                    <RadioGroup
                        aria-labelledby="shipping-address-label"
                        name="shipping-address-choice"
                        value={shipToBillingAddress}
                        onChange={e =>
                            setShipToBillingAddress(
                                JSON.parse((e.target as HTMLInputElement).value)
                            )
                        }
                    >
                        <FormControlLabel
                            value="true"
                            control={
                                <Radio
                                    {...register('shipToBillingAddress')}
                                    size="small"
                                    sx={{
                                        color: 'black',
                                        '&.Mui-checked': {
                                            color: 'black',
                                        },
                                    }}
                                />
                            }
                            label={getLabelFallback(
                                'MYPROFILE_PERSONAL_DETAILS_PRIMARY_FORM_SHIP_TO_BILLING_ADDRESS'
                            )}
                        />
                        <FormControlLabel
                            value="false"
                            control={
                                <Radio
                                    {...register('shipToBillingAddress')}
                                    size="small"
                                    sx={{
                                        color: 'black',
                                        '&.Mui-checked': {
                                            color: 'black',
                                        },
                                    }}
                                />
                            }
                            label={getLabelFallback(
                                'MYPROFILE_PERSONAL_DETAILS_PRIMARY_FORM_SHIP_TO_ANOTHER_ADDRESS'
                            )}
                        />
                    </RadioGroup>
                </FormControl>
                {!shipToBillingAddress && (
                    <div className="form-group form-group_shipping-another-address">
                        <div className="form-group form-group_firstname">
                            <TextField
                                {...register('shippingFirstName')}
                                className={`${
                                    errors?.shippingFirstName?.message
                                        ? 'form-field-error'
                                        : formFieldStatus.shippingFirstName.success
                                        ? 'form-field-success'
                                        : ''
                                }`}
                                name="shippingFirstName"
                                variant="outlined"
                                label={capitalizeFirstLetter(
                                    getLabelFallback(
                                        'MYPROFILE_PERSONAL_DETAILS_FORM_SHIPPINGFIRSTNAME_LABEL'
                                    )
                                )}
                                onChange={handleInputEvent}
                                onFocus={handleInputEvent}
                                onBlur={handleInputEvent}
                                helperText={errors?.shippingFirstName?.message}
                                InputProps={{
                                    endAdornment: formFieldStatus.shippingFirstName.focus ? (
                                        <InputAdornment position="end" sx={{ width: '25px' }}>
                                            <CloseIcon
                                                className="cursor-pointer"
                                                onMouseDown={() =>
                                                    setValue('shippingFirstName', '')
                                                }
                                            />
                                        </InputAdornment>
                                    ) : errors?.shippingFirstName?.message ? (
                                        <InputAdornment position="end" sx={{ width: '25px' }}>
                                            <ErrorIcon />
                                        </InputAdornment>
                                    ) : formFieldStatus.shippingFirstName.success ? (
                                        <InputAdornment position="end" sx={{ width: '25px' }}>
                                            <SuccessIcon />
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />
                            <TextField
                                {...register('shippingLastName')}
                                className={`${
                                    errors?.shippingLastName?.message
                                        ? 'form-field-error'
                                        : formFieldStatus.shippingLastName.success
                                        ? 'form-field-success'
                                        : ''
                                }`}
                                name="shippingLastName"
                                variant="outlined"
                                label={capitalizeFirstLetter(
                                    getLabelFallback(
                                        'MYPROFILE_PERSONAL_DETAILS_FORM_SHIPPINGLASTNAME_LABEL'
                                    )
                                )}
                                onChange={handleInputEvent}
                                onFocus={handleInputEvent}
                                onBlur={handleInputEvent}
                                helperText={errors?.shippingLastName?.message}
                                InputProps={{
                                    endAdornment: formFieldStatus.shippingLastName.focus ? (
                                        <InputAdornment position="end" sx={{ width: '25px' }}>
                                            <CloseIcon
                                                className="cursor-pointer"
                                                onMouseDown={() => setValue('shippingLastName', '')}
                                            />
                                        </InputAdornment>
                                    ) : errors?.shippingLastName?.message ? (
                                        <InputAdornment position="end" sx={{ width: '25px' }}>
                                            <ErrorIcon />
                                        </InputAdornment>
                                    ) : formFieldStatus.shippingLastName.success ? (
                                        <InputAdornment position="end" sx={{ width: '25px' }}>
                                            <SuccessIcon />
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />
                        </div>
                        <div className="form-group_billing-address">
                            <TextField
                                {...register('shippingAddressLine1')}
                                className={`address-line_1 ${
                                    errors?.shippingAddressLine1?.message
                                        ? 'form-field-error'
                                        : formFieldStatus.shippingAddressLine1.success
                                        ? 'form-field-success'
                                        : ''
                                }`}
                                name="shippingAddressLine1"
                                variant="outlined"
                                label={capitalizeFirstLetter(
                                    replaceLabelPlaceholder(
                                        'MYPROFILE_PERSONAL_DETAILS_PRIMARY_FORM_SHIPPING_ADDRESS_LINE',
                                        '{LINE_NUMBER}',
                                        '1'
                                    )
                                )}
                                onChange={handleInputEvent}
                                onFocus={handleInputEvent}
                                onBlur={handleInputEvent}
                                helperText={errors?.shippingAddressLine1?.message}
                                InputProps={{
                                    endAdornment: formFieldStatus.shippingAddressLine1.focus ? (
                                        <InputAdornment position="end" sx={{ width: '25px' }}>
                                            <CloseIcon
                                                className="cursor-pointer"
                                                onMouseDown={() =>
                                                    setValue('shippingAddressLine1', '')
                                                }
                                            />
                                        </InputAdornment>
                                    ) : errors?.shippingAddressLine1?.message ? (
                                        <InputAdornment position="end" sx={{ width: '25px' }}>
                                            <ErrorIcon />
                                        </InputAdornment>
                                    ) : formFieldStatus.shippingAddressLine1.success ? (
                                        <InputAdornment position="end" sx={{ width: '25px' }}>
                                            <SuccessIcon />
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />
                            {!showFormFields.shippingAddressLine && (
                                <a
                                    className="mb-32"
                                    onClick={() =>
                                        setShowFormFields({
                                            ...showFormFields,
                                            shippingAddressLine: true,
                                        })
                                    }
                                >
                                    {getLabelFallback(
                                        'MYPROFILE_PERSONAL_DETAILS_PRIMARY_FORM_SHIPPING_ADD_SECOND_ADDRESS_CTA'
                                    )}
                                </a>
                            )}
                            {showFormFields.shippingAddressLine && (
                                <TextField
                                    {...register('shippingAddressLine2')}
                                    className={`address-line_2 ${
                                        formFieldStatus.shippingAddressLine2.success
                                            ? 'form-field-success'
                                            : ''
                                    }`}
                                    name="shippingAddressLine2"
                                    variant="outlined"
                                    label={capitalizeFirstLetter(
                                        replaceLabelPlaceholder(
                                            'MYPROFILE_PERSONAL_DETAILS_PRIMARY_FORM_SHIPPING_ADDRESS_LINE',
                                            '{LINE_NUMBER}',
                                            '2'
                                        )
                                    )}
                                    value={watchShippingAddressLine2}
                                    onChange={handleInputEvent}
                                    onFocus={handleInputEvent}
                                    onBlur={handleInputEvent}
                                    InputProps={{
                                        endAdornment:
                                            formFieldStatus.shippingAddressLine2.focus &&
                                            watchShippingAddressLine2 ? (
                                                <InputAdornment
                                                    position="end"
                                                    sx={{ width: '25px' }}
                                                >
                                                    <CloseIcon
                                                        className="cursor-pointer"
                                                        onMouseDown={() =>
                                                            setValue('shippingAddressLine2', '')
                                                        }
                                                    />
                                                </InputAdornment>
                                            ) : formFieldStatus.shippingAddressLine2.success ? (
                                                <InputAdornment
                                                    position="end"
                                                    sx={{ width: '25px' }}
                                                >
                                                    <SuccessIcon />
                                                </InputAdornment>
                                            ) : null,
                                    }}
                                />
                            )}
                            <TextField
                                {...register('shippingZipcode')}
                                className={`mb-40 small-field ${
                                    errors?.shippingZipcode?.message
                                        ? 'form-field-error'
                                        : formFieldStatus.shippingZipcode.success
                                        ? 'form-field-success'
                                        : ''
                                }`}
                                onChange={handleInputEvent}
                                onFocus={handleInputEvent}
                                onBlur={handleInputEvent}
                                name="shippingZipcode"
                                variant="outlined"
                                type={`${
                                    config.countryName.toUpperCase() === 'CA' ? 'text' : 'number'
                                }`}
                                label={getLabelFallback(
                                    config.countryName.toUpperCase() === 'CA'
                                        ? 'MYPROFILE_PERSONAL_DETAILS_FORM_SHIPPINGPOSTALCODE_CA_LABEL'
                                        : 'MYPROFILE_PERSONAL_DETAILS_FORM_SHIPPINGZIPCODE_OTHER_COUNTRIES_LABEL'
                                )}
                                value={watchShippingZipCode}
                                helperText={errors?.shippingZipcode?.message}
                                inputProps={{
                                    minLength: `${
                                        config.countryName.toUpperCase() === 'CA' ? 7 : 5
                                    }`,
                                    maxLength: `${
                                        config.countryName.toUpperCase() === 'CA' ? 7 : 5
                                    }`,
                                }}
                                InputProps={{
                                    endAdornment: formFieldStatus.shippingZipcode.focus ? (
                                        <InputAdornment position="end" sx={{ width: '25px' }}>
                                            <CloseIcon
                                                className="cursor-pointer"
                                                onMouseDown={() => setValue('shippingZipcode', '')}
                                            />
                                        </InputAdornment>
                                    ) : errors?.shippingZipcode?.message ? (
                                        <InputAdornment position="end" sx={{ width: '25px' }}>
                                            <ErrorIcon />
                                        </InputAdornment>
                                    ) : formFieldStatus.shippingZipcode.success ? (
                                        <InputAdornment position="end" sx={{ width: '25px' }}>
                                            <SuccessIcon />
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />
                            <div className="billing-address_city">
                                <TextField
                                    {...register('shippingCity')}
                                    className={`${
                                        errors?.shippingCity?.message
                                            ? 'form-field-error'
                                            : formFieldStatus.shippingCity.success
                                            ? 'form-field-success'
                                            : ''
                                    }`}
                                    value={watchShippingCity}
                                    onChange={handleInputEvent}
                                    onFocus={handleInputEvent}
                                    onBlur={handleInputEvent}
                                    name="shippingCity"
                                    variant="outlined"
                                    label={capitalizeFirstLetter(
                                        getLabelFallback(
                                            'MYPROFILE_PERSONAL_DETAILS_FORM_SHIPPINGCITY_LABEL'
                                        )
                                    )}
                                    helperText={errors?.shippingCity?.message}
                                    InputProps={{
                                        endAdornment: formFieldStatus.shippingCity.focus ? (
                                            <InputAdornment position="end" sx={{ width: '25px' }}>
                                                <CloseIcon
                                                    className="cursor-pointer"
                                                    onMouseDown={() => setValue('shippingCity', '')}
                                                />
                                            </InputAdornment>
                                        ) : errors?.shippingCity?.message ? (
                                            <InputAdornment position="end" sx={{ width: '25px' }}>
                                                <ErrorIcon />
                                            </InputAdornment>
                                        ) : formFieldStatus.shippingCity.success ? (
                                            <InputAdornment position="end" sx={{ width: '25px' }}>
                                                <SuccessIcon />
                                            </InputAdornment>
                                        ) : null,
                                    }}
                                />
                                <Controller
                                    name="shippingState"
                                    control={control}
                                    render={({ field: { value }, fieldState, field }) => (
                                        <FormControl>
                                            <InputLabel id="shipping-state-label">
                                                {capitalizeFirstLetter(
                                                    getLabelFallback(
                                                        config.countryName === 'CA'
                                                            ? 'MYPROFILE_PERSONAL_DETAILS_PRIMARY_FORM_SHIPPING_PROVINCES_LABEL'
                                                            : 'MYPROFILE_PERSONAL_DETAILS_PRIMARY_FORM_SHIPPING_STATE_LABEL'
                                                    )
                                                )}
                                            </InputLabel>
                                            <Select
                                                className={`billing-state ${
                                                    fieldState?.error?.message
                                                        ? 'form-field-error'
                                                        : value
                                                        ? 'form-field-success'
                                                        : ''
                                                }`}
                                                name="shippingState"
                                                labelId="shipping-state-label"
                                                variant="outlined"
                                                IconComponent={props => <ArrowDown {...props} />}
                                                label={capitalizeFirstLetter(
                                                    getLabelFallback(
                                                        config.countryName === 'CA'
                                                            ? 'MYPROFILE_PERSONAL_DETAILS_PRIMARY_FORM_SHIPPING_PROVINCES_LABEL'
                                                            : 'MYPROFILE_PERSONAL_DETAILS_PRIMARY_FORM_SHIPPING_STATE_LABEL'
                                                    )
                                                )}
                                                endAdornment={
                                                    fieldState?.error?.message ? (
                                                        <InputAdornment
                                                            position="end"
                                                            sx={{
                                                                width: '25px',
                                                                position: 'absolute',
                                                                right: '45px',
                                                            }}
                                                        >
                                                            <ErrorIcon />
                                                        </InputAdornment>
                                                    ) : value ? (
                                                        <InputAdornment
                                                            position="end"
                                                            sx={{
                                                                width: '25px',
                                                                position: 'absolute',
                                                                right: '45px',
                                                            }}
                                                        >
                                                            <SuccessIcon />
                                                        </InputAdornment>
                                                    ) : null
                                                }
                                                {...field}
                                            >
                                                {config.stateList.map(option => (
                                                    <MenuItem
                                                        key={option.code}
                                                        value={option.code}
                                                        disabled={option.disabled}
                                                    >
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {errors?.shippingState?.message && (
                                                <FormHelperText className="error-helper-text">
                                                    {errors?.shippingState?.message}
                                                </FormHelperText>
                                            )}
                                        </FormControl>
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
export default PersonalDetailsPrimaryForm;
